import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

type GuideSection = {
  id: string;
  label: string;
};

type GuideTone = 'light' | 'dark';

function collectSections(): GuideSection[] {
  return Array.from(document.querySelectorAll<HTMLElement>('[data-guide-label][id]'))
    .map((el) => ({
      id: el.id,
      label: el.dataset.guideLabel?.trim() || el.id,
    }))
    .filter((section, index, all) => section.id && all.findIndex((item) => item.id === section.id) === index);
}

function colorIsTransparent(value: string): boolean {
  if (!value || value === 'transparent') return true;
  const match = value.match(/rgba?\(([^)]+)\)/);
  if (!match) return false;
  const parts = match[1].split(',').map((part) => Number.parseFloat(part.trim()));
  return parts.length === 4 && parts[3] === 0;
}

function colorIsDark(value: string): boolean {
  const match = value.match(/rgba?\(([^)]+)\)/);
  if (!match) return false;
  const [red, green, blue] = match[1].split(',').map((part) => Number.parseFloat(part.trim()));
  if (![red, green, blue].every(Number.isFinite)) return false;
  const luminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;
  return luminance < 0.42;
}

function sectionTone(id: string | null): GuideTone {
  let el = id ? document.getElementById(id) : null;
  while (el) {
    const background = window.getComputedStyle(el).backgroundColor;
    if (!colorIsTransparent(background)) {
      return colorIsDark(background) ? 'dark' : 'light';
    }
    el = el.parentElement;
  }

  return colorIsDark(window.getComputedStyle(document.body).backgroundColor) ? 'dark' : 'light';
}

function numericCssLength(value: string): number {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function guideActivationOffset(sections: GuideSection[]): number {
  const rootOffset = numericCssLength(window.getComputedStyle(document.documentElement).scrollPaddingTop);
  const firstSection = sections.map((section) => document.getElementById(section.id)).find(Boolean);
  const sectionOffset = firstSection ? numericCssLength(window.getComputedStyle(firstSection).scrollMarginTop) : 0;
  return rootOffset + sectionOffset + 12;
}

function pageIsAtBottom(): boolean {
  const documentHeight = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
  return window.scrollY + window.innerHeight >= documentHeight - 2;
}

function sectionTop(id: string): number | null {
  const el = document.getElementById(id);
  if (!el) return null;
  return el.getBoundingClientRect().top + window.scrollY;
}

function activeSectionAtTop(sections: GuideSection[]): string | null {
  if (!sections.length) return null;
  if (pageIsAtBottom()) return sections[sections.length - 1]?.id ?? null;

  const activationLine = window.scrollY + guideActivationOffset(sections);
  let active = sections[0].id;

  for (const section of sections) {
    const top = sectionTop(section.id);
    if (top === null) continue;
    if (top > activationLine) break;
    active = section.id;
  }

  return active;
}

export function PageGuide() {
  const location = useLocation();
  const [sections, setSections] = useState<GuideSection[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [tone, setTone] = useState<GuideTone>('light');
  const pendingJumpId = useRef<string | null>(null);
  const pendingJumpTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    let frame = 0;
    const refresh = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const next = collectSections();
        setSections(next);
        setActiveId((current) => (current && next.some((item) => item.id === current) ? current : next[0]?.id ?? null));
      });
    };

    refresh();
    const observer = new MutationObserver(refresh);
    observer.observe(document.querySelector('main') ?? document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (sections.length < 2) return;

    const clearPendingJumpAfterScroll = () => {
      window.clearTimeout(pendingJumpTimer.current);
      pendingJumpTimer.current = window.setTimeout(() => {
        pendingJumpId.current = null;
      }, 180);
    };

    const updateActive = () => {
      if (pendingJumpId.current) {
        clearPendingJumpAfterScroll();
        return;
      }

      setActiveId(activeSectionAtTop(sections));
    };

    updateActive();
    window.addEventListener('scroll', updateActive, { passive: true });
    window.addEventListener('resize', updateActive);

    return () => {
      window.clearTimeout(pendingJumpTimer.current);
      window.removeEventListener('scroll', updateActive);
      window.removeEventListener('resize', updateActive);
    };
  }, [sections]);

  useEffect(() => {
    setTone(sectionTone(activeId));
  }, [activeId, sections]);

  if (sections.length < 2) return null;

  function jumpTo(id: string) {
    pendingJumpId.current = id;
    window.clearTimeout(pendingJumpTimer.current);
    pendingJumpTimer.current = window.setTimeout(() => {
      pendingJumpId.current = null;
    }, 900);
    setActiveId(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <nav
      aria-label="Page sections"
      className="fixed right-5 top-1/2 z-40 hidden -translate-y-1/2 flex-col gap-3.5 xl:flex"
    >
      {sections.map((section) => {
        const isActive = activeId === section.id;
        const isHovered = hoveredId === section.id;
        const showLabel = isActive || isHovered;
        const labelClass = tone === 'dark' ? 'text-paper opacity-80' : 'text-ink opacity-70';
        const hoverLineClass = tone === 'dark' ? 'bg-paper/70' : 'bg-ink/60';
        const idleLineClass = tone === 'dark' ? 'bg-paper/35' : 'bg-ink/25';

        return (
          <button
            key={section.id}
            type="button"
            onClick={() => jumpTo(section.id)}
            onMouseEnter={() => setHoveredId(section.id)}
            onMouseLeave={() => setHoveredId(null)}
            onFocus={() => setHoveredId(section.id)}
            onBlur={() => setHoveredId(null)}
            aria-label={`Jump to ${section.label}`}
            aria-current={isActive ? 'true' : undefined}
            className="group/guide flex cursor-pointer items-center justify-end gap-3 focus:outline-none"
          >
            <span
              className={`select-none whitespace-nowrap font-display text-[0.7rem] italic tracking-wide transition-opacity duration-200 ${
                showLabel ? labelClass : 'opacity-0'
              }`}
            >
              {section.label}
            </span>
            <span
              className={`block h-px transition-all duration-200 ${
                isActive
                  ? 'w-7 bg-accent'
                : isHovered
                    ? `w-5 ${hoverLineClass}`
                    : `w-3 ${idleLineClass}`
              }`}
            />
          </button>
        );
      })}
    </nav>
  );
}
